<template>
  <v-container fluid class="mt-5">
    <h1 class="text-h4 mb-4">{{ isEditMode ? 'Редактирование проекта' : 'Создание проекта' }}</h1>

    <v-alert
      v-if="showAlert"
      :type="alertType"
      closable
      @click:close="showAlert = false"
      class="mb-4"
    >
      {{ alertMessage }}
    </v-alert>

    <!-- Main Card with Tabs -->
    <v-card>
      <v-form ref="formRef" @submit.prevent="onSubmit">
        <v-tabs v-model="currentTab" bg-color="grey-lighten-4" color="primary">
          <v-tab value="basic">
            <v-icon start>mdi-information</v-icon>
            Основное
          </v-tab>
          <v-tab value="monitoring">
            <v-icon start>mdi-target</v-icon>
            Мониторинг
          </v-tab>
          <v-tab value="filters">
            <v-icon start>mdi-filter-cog</v-icon>
            Фильтры
          </v-tab>
        </v-tabs>

        <v-card-text>
          <v-window v-model="currentTab">
            <!-- Tab 1: Basic Information -->
            <v-window-item value="basic">
              <div class="py-4">
                <v-text-field
                  v-model="form.name"
                  label="Название проекта"
                  variant="outlined"
                  :rules="[rules.required]"
                  class="mb-4"
                ></v-text-field>

                <v-textarea
                  v-model="form.description"
                  label="Описание"
                  hint="Опциональное описание проекта"
                  persistent-hint
                  rows="3"
                  variant="outlined"
                  class="mb-4"
                ></v-textarea>

                <!--
                  TODO: Monitoring Mode Selection — hidden until we have a knowledge base
                  (Wikipedia integration, Wordstat API, or custom entity catalog).
                  Without a reference catalog, monitoring objects are functionally identical
                  to search queries and confuse users. See task #41 (Wordstat).
                  When re-enabling, restore monitoring_mode chip-group (queries_only / objects_only / both).
                -->

                <!-- Tags -->
                <v-card variant="outlined">
                  <v-card-title class="bg-teal-lighten-5">
                    <v-icon start>mdi-tag</v-icon>
                    Теги
                  </v-card-title>
                  <v-card-text>
                    <TagSelector
                      v-model="form.tags"
                    />
                  </v-card-text>
                </v-card>

              </div>
            </v-window-item>

            <!-- Tab 2: Monitoring (Queries and/or Objects) -->
            <v-window-item value="monitoring">
              <div class="py-4">
                <!-- Queries Section -->
                <v-card
                  class="mb-4"
                  variant="outlined"
                >
                  <v-card-title class="bg-blue-lighten-5">
                    <v-icon start>mdi-magnify</v-icon>
                    Поисковые запросы
                  </v-card-title>
                  <v-card-text>
                    <QueryBuilder
                      v-model="form.queries"
                      :search-in="form.search_mode"
                    />
                  </v-card-text>
                </v-card>

                <!-- Change tracking (content re-crawl + edit detection) -->
                <v-card class="mb-4" variant="outlined">
                  <v-card-title class="bg-blue-lighten-5">
                    <v-icon start>mdi-file-document-refresh-outline</v-icon>
                    Отслеживание изменений
                  </v-card-title>
                  <v-card-text class="pt-6">
                    <v-select
                      v-model="form.recrawl_window_hours"
                      :items="recrawlWindowOptions"
                      item-title="text"
                      item-value="value"
                      label="Временное окно"
                      variant="outlined"
                      density="comfortable"
                      hide-details="auto"
                    ></v-select>
                    <p class="text-caption text-medium-emphasis mt-2 mb-0">
                      Отслеживание изменений в диапазоне заданного временного окна.
                      Пусто = выключено.
                    </p>
                  </v-card-text>
                </v-card>

                <!--
                  TODO: Monitoring Objects Section — hidden until knowledge base is available.
                  Re-enable ObjectSelector when Wikipedia/Wordstat integration is ready.
                  Backend code (MonitoringObject, ProjectMonitoringObject, suggestions API) is preserved.
                -->
              </div>
            </v-window-item>

            <!-- Tab 3: Filters (Source Filters, Exclusions, Tags) -->
            <v-window-item value="filters">
              <div class="py-4">
                <!-- Source Scope — what to watch -->
                <v-card class="mb-4" variant="outlined">
                  <v-card-title class="bg-teal-lighten-5">
                    <v-icon start>mdi-eye-settings</v-icon>
                    За чем следить
                    <HelpHint section="projects" anchor="form" text="Где искать: во всех источниках или только в ваших" />
                  </v-card-title>
                  <v-card-text>
                    <v-radio-group
                      v-model="form.source_filters.source_scope"
                      density="compact"
                      hide-details
                    >
                      <v-radio
                        value="all"
                        label="Все источники (мои + общедоступные)"
                      />
                      <v-radio
                        value="own_only"
                        label="Только мои источники"
                      />
                      <v-radio
                        value="selected"
                        label="Выборка из моих источников"
                      />
                      <v-radio
                        value="public_only"
                        label="Только общедоступные"
                      />
                      <v-radio
                        value="public_plus_selected_own"
                        label="Общедоступные + выборка из моих"
                      />
                    </v-radio-group>

                    <v-expand-transition>
                      <div
                        v-if="needsUserSourcesSelector"
                        class="mt-4"
                      >
                        <UserSourcesSelector
                          v-model="form.user_source_ids"
                        />
                      </div>
                    </v-expand-transition>

                    <v-divider class="my-4" />

                    <v-checkbox
                      v-model="form.source_filters.include_own_unfiltered"
                      :disabled="!ownSourcesPossible"
                      density="compact"
                      hide-details
                      label="Сообщения из моих источников добавлять в результаты целиком (без фильтрации запросами)"
                    />
                    <div class="text-caption text-medium-emphasis ml-8">
                      Фильтры и запросы продолжают применяться к общедоступным источникам.
                    </div>
                  </v-card-text>
                </v-card>

                <!-- Source Filters Section -->
                <v-card class="mb-4" variant="outlined">
                  <v-card-title class="bg-purple-lighten-5">
                    <v-icon start>mdi-filter</v-icon>
                    Фильтры источников
                  </v-card-title>
                  <v-card-text>
                    <SourceFilters
                      v-model="form.source_filters"
                      :open-panels="uiState.sourceFilterPanels"
                      @update:open-panels="onSourcePanelsChange"
                    />
                  </v-card-text>
                </v-card>

                <!-- Exclusions Section -->
                <v-card class="mb-4" variant="outlined">
                  <v-card-title class="bg-orange-lighten-5">
                    <v-icon start>mdi-cancel</v-icon>
                    Исключения
                  </v-card-title>
                  <v-card-text>
                    <ExclusionSettings
                      v-model:sources="form.exclude_sources"
                      v-model:authors="form.exclude_authors"
                    />
                  </v-card-text>
                </v-card>

                <!-- Tags moved to "Основное" tab -->
              </div>
            </v-window-item>
          </v-window>

          <!-- Run Project Checkbox (only for creation) -->
          <v-checkbox
            v-if="!isEditMode"
            v-model="form.run_project"
            label="Запустить проект после создания"
            class="mb-4 font-weight-bold"
          ></v-checkbox>

          <!-- Action Buttons -->
          <div class="d-flex gap-2">
            <v-btn
              type="submit"
              color="primary"
              size="large"
              :loading="isSubmitting"
              :disabled="isSubmitting"
            >
              <v-icon start>mdi-content-save</v-icon>
              Сохранить
            </v-btn>
            <v-btn
              type="button"
              variant="outlined"
              size="large"
              @click="cancel"
              :disabled="isSubmitting"
            >
              <v-icon start>mdi-cancel</v-icon>
              Отмена
            </v-btn>
          </div>
        </v-card-text>
      </v-form>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import apiClient from '@/apiClient';
import { currentSpace, setSpace, userKeyOf } from '@/utils/space';
import QueryBuilder from '@/components/projects/form/QueryBuilder.vue';
// TODO: ObjectSelector hidden until knowledge base is available (Wikipedia/Wordstat)
// import ObjectSelector from '@/components/projects/form/ObjectSelector.vue';
import SourceFilters from '@/components/projects/form/SourceFilters.vue';
import ExclusionSettings from '@/components/projects/form/ExclusionSettings.vue';
import TagSelector from '@/components/projects/form/TagSelector.vue';
import UserSourcesSelector from '@/components/projects/form/UserSourcesSelector.vue';

// Router and store
const router = useRouter();
const route = useRoute();
const store = useStore();

// Check if edit mode
const isEditMode = computed(() => !!route.params.projectId);

// --- UI State persistence per project (localStorage) ---
const uiStateKey = computed(() => {
  const id = route.params.projectId || 'new';
  return `project_form_ui_${id}`;
});

const loadUiState = () => {
  try {
    const saved = localStorage.getItem(uiStateKey.value);
    if (saved) return JSON.parse(saved);
  } catch (e) { /* ignore */ }
  return null;
};

const savedUi = loadUiState();

const uiState = ref({
  sourceFilterPanels: savedUi?.sourceFilterPanels ?? ['level'],
});

const saveUiState = () => {
  try {
    localStorage.setItem(uiStateKey.value, JSON.stringify({
      currentTab: currentTab.value,
      sourceFilterPanels: uiState.value.sourceFilterPanels,
    }));
  } catch (e) { /* ignore */ }
};

const onSourcePanelsChange = (panels) => {
  uiState.value.sourceFilterPanels = panels;
  saveUiState();
};

// Refs
const formRef = ref(null);
const isSubmitting = ref(false);
const showAlert = ref(false);
const alertMessage = ref('');
const alertType = ref('error');
const currentTab = ref(savedUi?.currentTab || 'basic');

// Save UI state when tab changes
watch(currentTab, () => {
  saveUiState();
});

// Recrawl interval presets (null = выкл)
const recrawlWindowOptions = [
  { text: 'Выключено', value: null },
  { text: '24 часа после публикации', value: 24 },
  { text: '48 часов после публикации', value: 48 },
  { text: '72 часа после публикации', value: 72 },
]

// Form data
const form = ref({
  name: '',
  description: '',
  monitoring_mode: 'queries_only',
  filter_mode: 'inclusive',
  search_mode: 'everywhere',
  highlight_mode: 'disabled',
  recrawl_window_hours: null,
  queries: [],
  monitoring_objects: [],
  source_filters: {
    source_scope: 'all',
    include_own_unfiltered: false,
    include_federal: true,
    include_regional: true,
    include_foreign_close: true,
    include_foreign_far: true,
    include_global: true,
    include_media_kinds: [],
    include_platforms: [],
    include_topics: [],
    include_geolocations: [],
    include_languages: [],
  },
  exclude_sources: [],
  exclude_authors: [],
  user_source_ids: [],
  tags: [],
  run_project: false,
});

// Validation rules
const rules = {
  required: value => !!value || 'Поле обязательно для заполнения',
};

// Show user-sources picker when scope mixes own sources with selection
const SCOPES_NEEDING_USER_SOURCES = ['selected', 'public_plus_selected_own'];
const needsUserSourcesSelector = computed(() =>
  SCOPES_NEEDING_USER_SOURCES.includes(form.value.source_filters.source_scope),
);

// Own-source unfiltered passthrough only matters when own sources can appear at all.
const SCOPES_WITHOUT_OWN_SOURCES = ['public_only'];
const ownSourcesPossible = computed(() =>
  !SCOPES_WITHOUT_OWN_SOURCES.includes(form.value.source_filters.source_scope),
);

// TODO: showQueriesSection / showObjectsSection — removed while monitoring_mode is hidden.
// Always show queries. Re-enable when knowledge base (Wikipedia/Wordstat) is available.

// Methods
const fetchProjectData = async () => {
  if (!route.params.projectId) return;

  try {
    const response = await apiClient.get(`/project/${route.params.projectId}`);
    const project = response.data;

    // A project of another space (opened by a link): the form works in its space - the sources and
    // tags it offers are those of the project, like the switcher, the page opens anew there.
    // An admin opening a project of someone else stays where he is.
    const key = userKeyOf(store.state.userInfo);
    const space = project.team_id ? String(project.team_id) : 'personal';
    if (key && 'team_id' in project && !store.getters.isAdmin && space !== currentSpace(key)) {
      setSpace(key, space);
      window.location.reload();
      return;
    }

    // Map API data to form
    form.value = {
      name: project.name || '',
      description: project.description || '',
      monitoring_mode: project.monitoring_mode || 'queries_only',
      filter_mode: project.filter_mode || 'inclusive',
      search_mode: project.search_mode || 'everywhere',
      highlight_mode: project.highlight_mode || 'disabled',
      recrawl_window_hours: project.recrawl_window_hours ?? null,
      queries: project.queries || [],
      monitoring_objects: project.monitoring_objects || [],
      source_filters: {
        source_scope: 'all',
        include_own_unfiltered: false,
        include_federal: true,
        include_regional: true,
        include_foreign_close: true,
        include_foreign_far: true,
        include_global: true,
        include_media_kinds: [],
        include_platforms: [],
        include_topics: [],
        include_geolocations: [],
        include_languages: [],
        ...(project.source_filters || {}),
      },
      exclude_sources: project.exclude_sources || [],
      exclude_authors: project.exclude_authors || [],
      user_source_ids: project.user_source_ids || [],
      tags: project.tags || [],
      run_project: false,
    };
  } catch (error) {
    console.error('Failed to fetch project:', error);
    showAlertMessage('Ошибка загрузки проекта', 'error');
  }
};

const showAlertMessage = (message, type = 'error') => {
  alertMessage.value = message;
  alertType.value = type;
  showAlert.value = true;
};

const validateForm = () => {
  // Validate based on monitoring mode
  // Queries are always required (monitoring_mode is forced to queries_only for now)
  if (!form.value.queries || form.value.queries.length === 0) {
    showAlertMessage('Добавьте хотя бы один запрос');
    currentTab.value = 'monitoring';
    return false;
  }

  return true;
};

const onSubmit = async () => {
  // Validate form
  const { valid } = await formRef.value.validate();
  if (!valid || !validateForm()) {
    return;
  }

  isSubmitting.value = true;

  try {
    const url = isEditMode.value
      ? `/project/${route.params.projectId}`
      : '/project';
    const method = isEditMode.value ? 'put' : 'post';

    // Adapt new form data to backend format
    // Legacy format for backward compatibility
    const projectQueries = form.value.queries
      .map(q => q.query_text)
      .filter(q => q && q.trim())
      .join('\n');

    // New format with full query objects
    const queries = form.value.queries
      .filter(q => q.query_text && q.query_text.trim())
      .map((q, index) => ({
        query_text: q.query_text.trim(),
        search_in: q.search_in,  // can be null (use project default)
        is_excluded: q.is_excluded || false,
        operator: q.operator || 'AND',
        weight: q.weight || 5,
        order_num: index,
      }));

    const payload = {
      projectName: form.value.name,
      projectQueries: projectQueries,  // Legacy - keep for now
      queries: queries,  // New format with all fields
      runProject: !isEditMode.value && form.value.run_project,
      description: form.value.description || '',
      monitoring_mode: form.value.monitoring_mode,
      filter_mode: form.value.filter_mode,
      search_mode: form.value.search_mode,
      highlight_mode: form.value.highlight_mode,
      recrawl_window_hours: form.value.recrawl_window_hours || null,
      source_filters: form.value.source_filters,
      exclude_sources: form.value.exclude_sources || [],
      exclude_authors: form.value.exclude_authors || [],
      user_source_ids: needsUserSourcesSelector.value
        ? (form.value.user_source_ids || [])
        : [],
      tags: form.value.tags || [],
      monitoring_objects: form.value.monitoring_objects || [],
    };

    // DEBUG: Log queries with search_in values
    console.log('=== FRONTEND DEBUG: Queries before sending ===');
    queries.forEach((q, i) => {
      console.log(`Query ${i}:`, {
        query_text: q.query_text,
        search_in: q.search_in,
        search_in_type: typeof q.search_in,
        search_in_is_null: q.search_in === null
      });
    });

    await apiClient[method](url, payload);

    store.commit('setProjectCreated', true);
    router.push('/projects');
  } catch (error) {
    console.error('Error saving project:', error);
    // the refusal of core comes in detail (HTTPException: the plan limit), older answers in message
    const data = error.response && error.response.data;
    const text = data && (typeof data.detail === 'string' ? data.detail : data.message);
    if (text) {
      showAlertMessage(text);
    } else {
      showAlertMessage('Ошибка сохранения проекта, попробуйте ещё раз!');
    }
  } finally {
    isSubmitting.value = false;
  }
};

const cancel = () => {
  router.push('/projects');
};

// Lifecycle
onMounted(() => {
  if (isEditMode.value) {
    fetchProjectData();
  }
});
</script>

<style scoped>
.gap-2 {
  gap: 0.5rem;
}
</style>
